import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Typography,
  Divider,
  Switch,
  FormControlLabel,
} from "@mui/material";
import api from "../../../services/api";
import AccessoryCustomizationField from "./AccessoryCustomizationField";
import AccessoryDisabledCombinationsForm from "./AccessoryDisabledCombinationsForm";
import SnackbarAlert from "../../../components/SnackbarAlert";

const AccessoryFormModal = ({
  open,
  onClose,
  onSave,
  accessory,
  subcategories,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    displayName: "",
    description: "",
    category: "accessory",
    subcategory: "",
    price: 0,
    stock: 1,
    images: [],
    isFeatured: false,
    isActive: true,
    notes: "",
    keywords: [],
    seo: {
      metaTitle: "",
      metaDescription: "",
      metaKeywords: [],
    },
    customizationOptions: [],
    disabledCombinations: [],
  });

  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  useEffect(() => {
    if (accessory) {
      setFormData({
        name: accessory.name || "",
        displayName: accessory.displayName || "",
        description: accessory.description || "",
        category: "accessory",
        subcategory: accessory.subcategory || "",
        price: accessory.price || 0,
        stock: accessory.stock ?? 1,
        images: accessory.images || [],
        isFeatured: accessory.isFeatured || false,
        isActive: accessory.isActive !== undefined ? accessory.isActive : true,
        notes: accessory.notes || "",
        keywords: accessory.keywords || [],
        seo: {
          metaTitle: accessory.seo?.metaTitle || "",
          metaDescription: accessory.seo?.metaDescription || "",
          metaKeywords: accessory.seo?.metaKeywords || [],
        },
        customizationOptions: accessory.customizationOptions || [],
        disabledCombinations: accessory.disabledCombinations || [],
      });
    }
  }, [accessory]);

  const handleSubmit = async () => {
    try {
      if (accessory) {
        await api.put(`/accessories/${accessory._id}`, formData);
        setSnackbarMessage("Accessory updated successfully");
      } else {
        await api.post("/accessories", formData);
        setSnackbarMessage("Accessory created successfully");
      }
      setShowSnackbar(true);
      setTimeout(() => {
        onSave();
        onClose();
      }, 1500);
    } catch (err) {
      console.error("Error saving accessory item:", err);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {accessory ? "Edit Accessory Item" : "Add Accessory Item"}
      </DialogTitle>
      <DialogContent dividers>
        <TextField
          fullWidth
          label="Name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          sx={{ my: 1 }}
        />
        <TextField
          fullWidth
          label="Display Name"
          value={formData.displayName}
          onChange={(e) =>
            setFormData({ ...formData, displayName: e.target.value })
          }
          sx={{ my: 1 }}
        />
        <TextField
          fullWidth
          label="Description"
          value={formData.description}
          onChange={(e) =>
            setFormData({ ...formData, description: e.target.value })
          }
          sx={{ my: 1 }}
        />
        <TextField
          fullWidth
          select
          label="Subcategory"
          value={formData.subcategory}
          onChange={(e) =>
            setFormData({ ...formData, subcategory: e.target.value })
          }
          sx={{ my: 1 }}>
          {subcategories.map((sub) => (
            <MenuItem key={sub} value={sub}>
              {sub}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          fullWidth
          type="number"
          label="Price"
          value={formData.price}
          onChange={(e) =>
            setFormData({ ...formData, price: parseFloat(e.target.value) })
          }
          sx={{ my: 1 }}
        />
        <TextField
          fullWidth
          type="number"
          label="Stock"
          value={formData.stock}
          onChange={(e) =>
            setFormData({ ...formData, stock: parseInt(e.target.value) })
          }
          sx={{ my: 1 }}
        />
        <TextField
          fullWidth
          label="Image URLs (comma separated)"
          value={formData.images.join(", ")}
          onChange={(e) =>
            setFormData({
              ...formData,
              images: e.target.value.split(",").map((url) => url.trim()),
            })
          }
          sx={{ my: 1 }}
        />

        <FormControlLabel
          control={
            <Switch
              checked={formData.isFeatured}
              onChange={(e) =>
                setFormData({ ...formData, isFeatured: e.target.checked })
              }
            />
          }
          label="Mark as Featured"
          sx={{ my: 1 }}
        />

        <FormControlLabel
          control={
            <Switch
              checked={formData.isActive}
              onChange={(e) =>
                setFormData({ ...formData, isActive: e.target.checked })
              }
            />
          }
          label="Is Active (Visible)"
          sx={{ my: 1 }}
        />

        <TextField
          fullWidth
          label="Notes"
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          sx={{ my: 1 }}
        />

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle1" gutterBottom>
          SEO Settings
        </Typography>
        <TextField
          fullWidth
          label="Meta Title"
          value={formData.seo.metaTitle}
          onChange={(e) =>
            setFormData({
              ...formData,
              seo: { ...formData.seo, metaTitle: e.target.value },
            })
          }
          sx={{ my: 1 }}
        />
        <TextField
          fullWidth
          label="Meta Description"
          value={formData.seo.metaDescription}
          onChange={(e) =>
            setFormData({
              ...formData,
              seo: { ...formData.seo, metaDescription: e.target.value },
            })
          }
          sx={{ my: 1 }}
        />
        <TextField
          fullWidth
          label="Meta Keywords (comma separated)"
          value={formData.seo.metaKeywords.join(", ")}
          onChange={(e) =>
            setFormData({
              ...formData,
              seo: {
                ...formData.seo,
                metaKeywords: e.target.value.split(",").map((kw) => kw.trim()),
              },
            })
          }
          sx={{ my: 1 }}
        />

        {/* Customization Section */}
        <Divider sx={{ my: 2 }} />
        <Typography fontWeight="bold" gutterBottom>
          Customization Options
        </Typography>
        {formData.customizationOptions.map((cust, index) => (
          <AccessoryCustomizationField
            key={index}
            customizationOptions={cust}
            onChange={(updated) => {
              const updatedCustomizations = [...formData.customizationOptions];
              updatedCustomizations[index] = updated;
              setFormData({
                ...formData,
                customizationOptions: updatedCustomizations,
              });
            }}
            onRemove={() => {
              const updated = [...formData.customizationOptions];
              updated.splice(index, 1);
              setFormData({
                ...formData,
                customizationOptions: updated,
              });
            }}
          />
        ))}
        <Button
          variant="outlined"
          onClick={() =>
            setFormData({
              ...formData,
              customizationOptions: [
                ...formData.customizationOptions,
                { type: "select", key: "", label: "", options: [] },
              ],
            })
          }
          sx={{ mt: 1 }}>
          + Add Customization
        </Button>

        {formData.customizationOptions.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography fontWeight="bold" gutterBottom>
              Disable Specific Option Combinations
            </Typography>
            <AccessoryDisabledCombinationsForm
              customizationOptions={formData.customizationOptions}
              disabledCombinations={formData.disabledCombinations}
              onChange={(updated) =>
                setFormData((prev) => ({
                  ...prev,
                  disabledCombinations: updated,
                }))
              }
            />
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained">
          Save
        </Button>
      </DialogActions>

      <SnackbarAlert
        open={showSnackbar}
        onClose={() => setShowSnackbar(false)}
        message={snackbarMessage}
        severity="success"
        bgColor="#4caf50"
        iconColor="#fff"
      />
    </Dialog>
  );
};

export default AccessoryFormModal;
